/* eslint-disable ember/no-computed-properties-in-native-classes */
import EmberObject, { computed, set } from '@ember/object';
import { alias } from '@ember/object/computed';
import Service, { service } from '@ember/service';
import type { AsyncBelongsTo } from '@ember-data/model';
import type Model from '@ember-data/model';
import type StoreService from '@ember-data/store';
import { tracked } from '@glimmer/tracking';
import Ember from 'ember';
import type AbilitiesService from 'ember-can/services/abilities';
import { dropTask, restartableTask, timeout, didCancel } from 'ember-concurrency';
import type CookiesService from 'ember-cookies/services/cookies';
import type MomentService from 'ember-moment/services/moment';
import config from 'garaje/config/environment';
import type DeviceModel from 'garaje/models/device';
import type LocationModel from 'garaje/models/location';
import type PrinterModel from 'garaje/models/printer';
import type CurrentLocationRoute from 'garaje/pods/current-location/route';
import type CurrentAdminService from 'garaje/services/current-admin';
import type DevicesManagerService from 'garaje/services/devices-manager';
import type { DeviceResponse } from 'garaje/services/devices-manager';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type IntegrationsManagerService from 'garaje/services/integrations-manager';
import type LocationsService from 'garaje/services/locations';
import type MetricsService from 'garaje/services/metrics';
import type PubnubService from 'garaje/services/pubnub';
import type { PubnubChannel, PubnubInstance } from 'garaje/services/pubnub';
import type SplitioService from 'garaje/services/splitio';
import type StateService from 'garaje/services/state';
import type WootricService from 'garaje/services/wootric';
import deviceStatus from 'garaje/utils/decorators/device-status';
import { getActiveEmployeeCategory, getActiveLocationsCategory } from 'garaje/utils/log-rocket-helpers';
import { loadHeadway, loadChameleon } from 'garaje/utils/script-loader';
import type { Permission } from 'garaje/utils/ui-permissions';
import zft from 'garaje/utils/zero-for-tests';
import $ from 'jquery';
import moment from 'moment-timezone';
import { resolve } from 'rsvp';

@deviceStatus
class DeviceStatus extends EmberObject {
  declare isStatusBad: boolean;
  declare isStatusWarn: boolean;
}

interface LocationOwnable extends Model {
  location: AsyncBelongsTo<LocationModel> | LocationModel;
}

interface NewInviteEvent {
  type: 'new_invite';
  invite: Record<string, unknown>;
}

interface DeleteInviteEvent {
  type: 'delete_invite';
  invite_id: string;
}

interface EntryEvent {
  event: 'unknown';
  v2entry: Record<string, unknown>;
}

interface HubEvent {
  event: 'hub_event_report';
  event_report: {
    id: string;
  };
}

type AllEvents = DeleteInviteEvent | NewInviteEvent | EntryEvent | HubEvent;

const METHOD_KEYS = [
  'save',
  'rollbackAttributes',
  'changedAttributes',
  'generateApiKey',
  'generatePreRegistrationLink',
  'generateSecurityDeskLink',
  'belongsTo',
] as const;

const currentLocation: Record<string, unknown> = {};

METHOD_KEYS.map(function (method) {
  currentLocation[method] = function (this: { content: LocationModel }) {
    const content = this.content;
    // @ts-ignore
    // eslint-disable-next-line prefer-rest-params
    return content[method](...arguments);
  };
});

interface SearchUsersOptions {
  term: string;
  roles?: string[];
  confirmedUsers?: boolean;
  permissions?: Permission[];
}

// @ts-ignore _ProxyMixin is private, has no types,  and marked for removal: https://deprecations.emberjs.com/id/deprecate-import--proxy-mixin-from-ember/
// eslint-disable-next-line ember/no-classic-classes
export default class CurrentLocationService extends Service.extend(Ember._ProxyMixin, currentLocation) {
  declare content: LocationModel | null;
  declare deviceStatus: string | null;

  @service declare abilities: AbilitiesService;
  @service declare cookies: CookiesService;
  @service declare currentAdmin: CurrentAdminService;
  @service declare devicesManager: DevicesManagerService;
  @service declare integrationsManager: IntegrationsManagerService;
  @service declare locations: LocationsService;
  @service declare moment: MomentService;
  @service declare pubnub: PubnubService;
  @service declare store: StoreService;
  @service declare state: StateService;
  @service declare splitio: SplitioService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare metrics: MetricsService;
  @service declare wootric: WootricService;

  @alias('content') location!: LocationModel;

  pubnubInstance: PubnubInstance | null = null;
  @tracked iPadVersions: string[] = [];
  @tracked pubnubConfig?: PubnubChannel;

  reset(): void {
    this.teardownPubnub();
    void this.loadIntegrations.cancelAll();
    void this.pubnub.fetchChannels.cancelAll();
    set(this, 'content', null);
  }

  setupPubnub(masterRoute: CurrentLocationRoute): void {
    if (!this.content) {
      return;
    }

    const devicesManager = this.devicesManager;
    const integrationsManager = this.integrationsManager;

    this.pubnub.fetchChannels
      .perform({
        locationId: this.location.id,
        userId: this.currentAdmin.id,
      })
      .then((pubnubConfig) => {
        this.pubnubConfig = pubnubConfig;
        const pubnub = this.pubnub.initialize(pubnubConfig.cipherKey, 'current-location');
        this.pubnubInstance = pubnub;

        pubnub.subscribe({
          channel: pubnubConfig.channelIntegrations,
          message: (data) => {
            const payload = <Record<string, unknown>>JSON.parse(data);

            integrationsManager.handleEvent(payload, this.location);
          },
        });

        pubnub.subscribe({
          channel: [pubnubConfig.channelEntry, pubnubConfig.channelInvite],
          message: (m) => {
            const payload = <AllEvents>JSON.parse(m);
            const isHubEvent = 'event' in payload && payload.event === 'hub_event_report';

            if (isHubEvent) {
              const loadedEventReport = this.store.peekRecord('event-report', payload?.event_report?.id);
              if (loadedEventReport) {
                this.store.pushPayload('event-report', {
                  data: {
                    type: 'event-reports',
                    id: payload.event_report.id,
                    attributes: payload.event_report,
                  },
                });
              }
              return;
            }

            if ('event' in payload && payload.event && !this.ignorePubnubEntryEvent()) {
              void masterRoute.pushEntry(<EntryEvent>payload);
            }

            if ('type' in payload && payload.type && payload.type === 'delete_invite') {
              masterRoute.deleteInvite(payload);
            } else if ('type' in payload && payload.type && !this.ignorePubnubInviteEvent()) {
              masterRoute.newInvite(payload);
            }
          },
        });

        pubnub.subscribe({
          channel: pubnubConfig.channelEmployeeVisits,
          message: (m) => {
            const payload = <AllEvents>JSON.parse(m);

            if ('event' in payload && payload.event && !this.ignorePubnubEmployeeEvent()) {
              void masterRoute.pushEntry(<EntryEvent>payload);
            }

            if ('type' in payload && payload.type && payload.type === 'delete_invite') {
              masterRoute.deleteInvite(payload);
            } else if ('type' in payload && payload.type && !this.ignorePubnubEmployeeEvent()) {
              masterRoute.newInvite(payload);
            }
          },
        });

        pubnub.subscribe({
          channel: pubnubConfig.channelDevices,
          message(data) {
            const payload = <DeviceResponse>JSON.parse(data);
            devicesManager.handleEvent(payload);
          },
        });
      })
      .catch((e) => {
        if (!didCancel(e)) {
          throw e;
        }
      });
  }

  teardownPubnub(): void {
    if (!this.content) {
      return;
    }

    const pubnubConfig = this.pubnubConfig;

    if (pubnubConfig) {
      this.pubnubInstance?.unsubscribe({
        channel: [
          pubnubConfig.channelEntry,
          pubnubConfig.channelInvite,
          pubnubConfig.channelDevices,
          pubnubConfig.channelIntegrations,
          pubnubConfig.channelEmployeeVisits,
        ],
      });
    }
  }

  loadIntegrations = dropTask(async () => {
    const { currentCompany } = this.state;
    await this.integrationsManager.loadIntegration(currentCompany.id);
  });

  setupHeadway(): void {
    if (!window.HW_config) {
      window.HW_config = {
        selector: '.updates-trigger',
        account: '9Jl95y',
        translations: {
          title: 'Latest Updates',
          readMore: 'Read more',
          labels: { new: 'New', improvements: 'Improvements', fix: 'Fixes' },
        },
        callbacks: {
          onWidgetReady: function (widget: { getUnseenCount: () => number }) {
            if (widget.getUnseenCount() === 0) {
              // eslint-disable-next-line ember/no-jquery, @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
              $('#HW_badge').hide();
            }
          },
          // eslint-disable-next-line ember/no-jquery, @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          onShowWidget: () => $('#HW_badge').hide(),
        },
      };
      loadHeadway();
    }
  }

  teardownHeadway(): void {
    const node = document.getElementById('envoyheadwayapp');
    if (node) {
      if (node.remove) {
        node.remove();
      } else if (node.parentNode) {
        node.parentNode.removeChild(node);
      }

      if (window.HW_config) {
        delete window.HW_config;
      }
    }
  }

  setupChameleon(): void {
    loadChameleon();
  }

  setTrackingData(isBoss: boolean): void {
    if (this.content && !isBoss && config.environment !== 'development') {
      this.setupSegment();
    }
  }

  // Setup for Intercom (via Segment)
  setupSegment(): void {
    const company = this.state.currentCompany;
    const vrSubscription = this.state.vrSubscription;
    const currentAdmin = this.currentAdmin;
    const metrics = this.metrics;
    const wootric = this.wootric;

    const userId = currentAdmin.id;

    const intercomIdentityHash = this.state.currentUser.intercomIdentityHash;
    const { cookieDomain: domain } = config;

    const userCreatedAtUnix = currentAdmin.createdAt ? moment(currentAdmin.createdAt).format('x') : null;

    const companyMetaState = this.state._companyMeta;
    const activeEmployeeCount = companyMetaState?.['employees-count'];
    const activeLocationsCount = companyMetaState?.['active-locations-count'];

    const activeEmployeesCategory = getActiveEmployeeCategory(activeEmployeeCount);
    const activeLocationsCategory = getActiveLocationsCategory(activeLocationsCount);

    const traits = {
      createdAt: userCreatedAtUnix,
      email: currentAdmin.email || null,
      employeeId: currentAdmin.employee?.id || null,
      name: currentAdmin.fullName || null,
      phone: currentAdmin.phoneNumber || null,
      url: window.location.href || null,
      last_dashboard_location_name: this.location.name || null,
      last_dashboard_location_id: this.location.id || null,
      last_dashboard_location_boss_link: `https://boss.envoy.com/locations/${this.location.id}` || null,
      has_role_last_dashboard_location_receptionist: currentAdmin.isReceptionist,
      has_role_last_dashboard_location_security: currentAdmin.isSecurity,
      has_role_last_dashboard_location_location_admin: currentAdmin.isLocationAdmin,
      has_role_last_dashboard_location_employee: currentAdmin.isEmployee,
      has_role_last_dashboard_company_billing: currentAdmin.isBillingAdmin,
      has_role_last_dashboard_company_global_admin: currentAdmin.isGlobalAdmin,
      has_role_any_location_security: currentAdmin.allLocationRoleNames.includes('Security'),
      has_role_any_location_location_admin: currentAdmin.allLocationRoleNames.includes('Location Admin'),
      has_role_any_location_receptionist: currentAdmin.allLocationRoleNames.includes('Receptionist'),
      has_role_any_location_employee: currentAdmin.allLocationRoleNames.includes('Employee'),
      company: {
        id: company.id || null,
        name: company.name || null,
        last_employee_directory_sync_at: company.syncedSince || null,
        plan: vrSubscription?.plan || null,
        vr_plan_name: vrSubscription?.plan || null,
        company_retool_link: `https://retool.envoy.christmas/presentation/Account%20%26%20User%20Explorer/Company#companyID=${company.id}`,
        vr_trial_end_at: vrSubscription?.hasTrialDaysLeft ? vrSubscription.trialEndDate || null : null,
      },
      'company.id': company.id || null,
      'company.name': company.name || null,
      'company.employee_count': activeEmployeeCount || null,
      'company.employee_count_category': activeEmployeesCategory || null,
      'company.active_locations_count': activeLocationsCount || null,
      'company.active_locations_count_category': activeLocationsCategory || null,
      'company.last_employee_directory_sync_at': company.syncedSince || null,
      'company.plan': vrSubscription?.plan || null,
      'company.vr_plan_name': vrSubscription?.plan || null,
      'company.company_retool_link': `https://retool.envoy.christmas/presentation/Account%20%26%20User%20Explorer/Company#companyID=${company.id}`,
    };

    metrics.identify(userId, traits);
    if (window.Intercom && window.intercomSettings?.['user_id'] !== userId) {
      window.Intercom('shutdown');
      window.intercomSettings = Object.assign({}, window.intercomSettings, {
        user_id: userId,
        user_hash: intercomIdentityHash,
      });
      window.Intercom('boot');
    }
    // Write a cookie for envoy.com and help.envoy.com to use the
    // Intercom messenger with identity verification on.
    this.cookies.write('intercomIdentityHash', intercomIdentityHash, { domain });

    if (!this.currentAdmin.isEmployee) {
      wootric.run(userId, traits);
    }
  }

  checkDevicesStatus = restartableTask(async () => {
    if (this.abilities.cannot('update devices') || !this.location.id) {
      return resolve();
    }

    const devices = await this.store.query('device', { filter: { location: this.location.id } });

    await this.setDeviceStatus(devices.slice());

    this.checkiPadVersions(devices.slice());

    return;
  });

  checkiPadVersions(devices: DeviceModel[]): void {
    const iPadVersions = devices
      .filter((device) => device.isIpad && device.isConnected)
      .map((device) => device.appVersion);
    this.iPadVersions = iPadVersions || [];
  }

  setDeviceStatus(devices: DeviceModel[]): Promise<void> {
    return this.printer.then((printerModel) => {
      const wrappedDevices = devices
        .filter((device) => {
          // if deviceType is not defined, it is probably a stale
          // device. We want to ignore since it won't even appear on
          // the user list.
          return device.hasType && device.isConnected;
        })
        .map((device) => {
          return DeviceStatus.create({
            // FF:device-status-pinging
            // need to pass in featureFlags becasue we are not looking up DeviceStatus class from the container
            // this will go away once we move device status onto models
            featureFlags: this.featureFlags,
            model: device,
            currentAppVersion: this.location.currentVersion,
            isPrinterEnabled: printerModel && printerModel.enabled,
          });
        });

      if (wrappedDevices.some((device) => device.isStatusBad)) {
        set(this, 'deviceStatus', 'bad');
      } else if (wrappedDevices.some((device) => device.isStatusWarn)) {
        set(this, 'deviceStatus', 'warn');
      } else {
        set(this, 'deviceStatus', null);
      }
    });
  }

  searchUsers = restartableTask(async (options: SearchUsersOptions) => {
    const {
      term,
      roles = ['Global Admin', 'Location Admin', 'Receptionist'],
      confirmedUsers = false,
      permissions,
    } = options;

    await timeout(zft(250));

    const result = await this.store.query('user-role', {
      filter: {
        query: term,
        location: this.location.id,
        roles: roles.join(','),
        confirmedUsers,
        permissions: permissions?.join(','),
      },
    });

    return result;
  });

  getUniqueUserRoleCount = restartableTask(
    async (
      term: string,
      roles: string[] = ['Global Admin', 'Location Admin', 'Receptionist'],
      confirmedUsers: boolean = true,
    ) => {
      await timeout(zft(250));

      const result = await this.store.query('user-role', {
        filter: { query: term, roles: roles.join(','), confirmedUsers },
      });

      const uniqueIds = new Set();
      result.map((role) => {
        const id = role.belongsTo('user').id();
        if (id !== undefined) {
          uniqueIds.add(id);
        }
      });

      return uniqueIds.size || 0;
    },
  );

  owns(model: LocationOwnable): boolean {
    return this.location.id === model.belongsTo('location').id();
  }

  getCorrectPrinter(): Promise<PrinterModel> {
    return this.store.queryRecord('printer', {
      filter: { device_config: this.location.belongsTo('config').id() },
    });
  }

  pollPrinter(): Promise<PrinterModel> {
    return this.getCorrectPrinter();
  }

  // These two ignore Pubnub functions are a temporary workaround to disable Pubnub updating the Entry or Invite log
  // when a custom role does not have the ability to view all Entries or Invites.
  // The correct fix will need to overhaul the v2/me/pubnub-channels API to return the correct channels based
  // on a user's permissions, along with the a cipherKey per channel rather than the same cipherKey for all
  // channels.
  ignorePubnubEntryEvent(): boolean {
    return !this.currentAdmin.isEmployee && this.abilities.cannot('view all entries');
  }

  ignorePubnubInviteEvent(): boolean {
    return !this.currentAdmin.isEmployee && this.abilities.cannot('view all invites');
  }

  ignorePubnubEmployeeEvent(): boolean {
    if (this.featureFlags.isEnabled('exclude-workplace-visits')) {
      return true;
    }
    if (this.currentAdmin.isEmployee) {
      return false;
    }
    return this.abilities.cannot('view employee invite in workplace');
  }

  @computed('location.printer', 'currentLocation.config')
  get printer(): Promise<PrinterModel> {
    return this.getCorrectPrinter();
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.
declare module '@ember/service' {
  interface Registry {
    'current-location': CurrentLocationService;
  }
}
